import { useCallback, useState } from "react";
import { useTranslation } from "react-i18next";
import { PageHeader } from "@/components/shared/PageHeader";
import { TableCard } from "@/components/shared/TableCard";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { DgiConsultaFlowModal } from "@/pages/dgi-documents/dgi-consulta-flow-modal";

export interface DgiDocumentRegistryRow {
  id: string;
  documentKey: string;
  titleKey: string;
  descriptionKey: string;
}

const REGISTRY: DgiDocumentRegistryRow[] = [
  {
    id: "duplicado-tarjeta-rut",
    documentKey: "duplicado-tarjeta-rut",
    titleKey: "dgiDocuments.registry.duplicadoTarjetaRut.title",
    descriptionKey: "dgiDocuments.registry.duplicadoTarjetaRut.description",
  },
];

export function DgiDocumentsPage(): JSX.Element {
  const { t } = useTranslation();
  const [modalOpen, setModalOpen] = useState(false);
  const [activeRow, setActiveRow] = useState<DgiDocumentRegistryRow | null>(null);
  const rows = REGISTRY;

  const handleObtener = useCallback((row: DgiDocumentRegistryRow): void => {
    setActiveRow(row);
    setModalOpen(true);
  }, []);

  const handleModalOpenChange = useCallback((open: boolean): void => {
    setModalOpen(open);
    if (!open) setActiveRow(null);
  }, []);

  return (
    <div className="space-y-4 fx-content">
      <PageHeader
        eyebrow={t("dgiDocuments.eyebrow", "Gestiones DGI")}
        title={t("dgiDocuments.pageTitle")}
        description={t("dgiDocuments.pageDescription")}
      />

      {/* Mobile cards */}
      <div className="space-y-3 md:hidden">
        {rows.map((row) => (
          <div
            key={row.id}
            className="rounded-lg border bg-card p-4 space-y-3"
          >
            <p className="font-medium text-foreground">{t(row.titleKey)}</p>
            <p className="text-sm text-muted-foreground">{t(row.descriptionKey)}</p>
            <Button
              type="button"
              size="sm"
              onClick={() => handleObtener(row)}
              className="w-full transition-colors duration-150"
            >
              {t("dgiDocuments.obtenerDocumento")}
            </Button>
          </div>
        ))}
      </div>

      {/* Desktop table */}
      <div className="hidden md:block">
        <TableCard>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="text-foreground">{t("dgiDocuments.columnDocument")}</TableHead>
                <TableHead className="text-foreground">{t("dgiDocuments.columnDescription")}</TableHead>
                <TableHead className="w-[1%] text-right text-foreground">
                  {t("dgiDocuments.columnAction")}
                </TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {rows.map((row) => (
                <TableRow key={row.id}>
                  <TableCell className="font-medium text-foreground">{t(row.titleKey)}</TableCell>
                  <TableCell className="text-muted-foreground">{t(row.descriptionKey)}</TableCell>
                  <TableCell className="text-right">
                    <Button
                      type="button"
                      size="sm"
                      onClick={() => handleObtener(row)}
                      className="transition-colors duration-150"
                    >
                      {t("dgiDocuments.obtenerDocumento")}
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableCard>
      </div>

      {activeRow ? (
        <DgiConsultaFlowModal
          open={modalOpen}
          onOpenChange={handleModalOpenChange}
          documentKey={activeRow.documentKey}
          documentTitle={t(activeRow.titleKey)}
        />
      ) : null}
    </div>
  );
}
